import { Component, OnDestroy, OnInit, signal } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import * as AuthActions from '../../modules/core/store/actions/auth.actions';
import * as fromApp from '../../modules/core/store/app.state';
import * as fromAuth from '@app/modules/core/store/reducers/auth.reducers';
import { LoginProcessState } from '../../modules/core/store/reducers/auth.reducers';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslocoService } from '@jsverse/transloco';
import { User } from '@app/modules/shared/interfaces/user.type';
import { ActivatedRoute, Router } from '@angular/router';
import { filter, Subject, take, takeUntil } from 'rxjs';
import { AuthService } from '@app/services/auth.service';
import { MatDialog } from '@angular/material/dialog';


@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss',
  standalone: false
})
export class LoginComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();

  user!: User;
  translation: any;

  // Signals for reactive UI state
  readonly hidePassword = signal(true);
  readonly formLoading = signal(false);
  readonly isForget = signal(false);

  loginForm = new FormGroup({
    username: new FormControl('', [Validators.required]),
    password: new FormControl('', Validators.required),
    rememberMe: new FormControl(true),
  });


  // Stripe colours — cyan → blue → indigo → violet → purple (matches reference image)
  // readonly stripes = [
  //   { hue: '190', sat: '85%', lt: '62%' },  // cyan
  //   { hue: '200', sat: '88%', lt: '60%' },
  //   { hue: '210', sat: '90%', lt: '58%' },  // sky-blue
  //   { hue: '218', sat: '88%', lt: '60%' },
  //   { hue: '226', sat: '85%', lt: '62%' },  // blue
  //   { hue: '234', sat: '80%', lt: '63%' },
  //   { hue: '242', sat: '78%', lt: '62%' },  // blue-indigo
  //   { hue: '250', sat: '75%', lt: '60%' },
  //   { hue: '258', sat: '72%', lt: '60%' },  // indigo
  //   { hue: '265', sat: '70%', lt: '62%' },
  //   { hue: '272', sat: '68%', lt: '64%' },  // violet
  //   { hue: '280', sat: '65%', lt: '65%' },
  //   { hue: '288', sat: '62%', lt: '66%' },  // purple
  //   { hue: '296', sat: '58%', lt: '67%' },  // soft magenta-purple
  // ];

  // Stripe colors for animated background (blue → indigo → purple gradient)
  readonly stripes = [
    { hue: '210', sat: '90%', lt: '65%' },
    { hue: '215', sat: '85%', lt: '68%' },
    { hue: '220', sat: '88%', lt: '70%' },
    { hue: '225', sat: '80%', lt: '72%' },
    { hue: '230', sat: '75%', lt: '74%' },
    { hue: '235', sat: '70%', lt: '76%' },
    { hue: '240', sat: '65%', lt: '78%' },
    { hue: '245', sat: '68%', lt: '75%' },
    { hue: '250', sat: '72%', lt: '72%' },
    { hue: '255', sat: '75%', lt: '70%' },
    // { hue: '260', sat: '78%', lt: '68%' },
    // { hue: '265', sat: '80%', lt: '65%' },
    // { hue: '270', sat: '75%', lt: '68%' },
    // { hue: '275', sat: '70%', lt: '70%' },
    // { hue: '280', sat: '65%', lt: '72%' },
    // { hue: '285', sat: '60%', lt: '75%' },
    // { hue: '290', sat: '55%', lt: '78%' },
    // { hue: '275', sat: '65%', lt: '76%' },
    // { hue: '260', sat: '70%', lt: '73%' },
    // { hue: '245', sat: '75%', lt: '70%' },
  ];

  constructor(
    private store: Store<fromApp.AppState>,
    private snackbar: MatSnackBar,
    private translocoService: TranslocoService,
    private router: Router,
    private route: ActivatedRoute,
    private authService: AuthService,
    private dialog: MatDialog,
  ) {}

  ngOnInit(): void {
    this.route.data.subscribe((data: any) => {
      if (data?.isForget === true) {
        this.isForget.set(true);
        this.loginForm.get('password')?.removeValidators(Validators.required);
        this.loginForm.get('password')?.updateValueAndValidity();
      } else {
        this.isForget.set(false);
        this.loginForm.get('password')?.setValidators(Validators.required);
        this.loginForm.get('password')?.updateValueAndValidity();
      }
    });

    // Redirect if already authenticated
    this.store.select('auth').pipe(take(1)).subscribe((authState: fromAuth.State) => {
      if (authState.user) {
        this.user = authState.user;
        if (this.user.isOrgAdmin || this.user.isOrgUser) {
          this.router.navigate(['/admin/dashboard']);
        }
        if (this.user.isCustomer) {
          this.router.navigate(['/tenant/dashboard']);
        }
        if (this.user.isSuperAdmin) {
          this.router.navigate(['/superadmin/organisations']);
        }
      }
    });

    // Watch login state for loading/error feedback
    this.store.select('auth').pipe(takeUntil(this.destroy$)).subscribe((authState) => {
      if (authState.loginProcessState === LoginProcessState.STARTED) {
        this.formLoading.set(true);
      } else if (authState.loginProcessState === LoginProcessState.FINISHED_SUCCESS) {
        this.formLoading.set(false);
      } else if (authState.loginProcessState === LoginProcessState.FINISHED_FAILED) {
        this.formLoading.set(false);
        this.snackbar.open(authState.errorMessage!, 'Dismiss', { duration: 4000 });
      }
    });

    this.translocoService.langChanges$.subscribe(lang => {
      this.translocoService.selectTranslation().subscribe(r => {
        this.translation = r;
      });
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  togglePassword(): void {
    this.hidePassword.update(v => !v);
  }

  onSubmit(): void {
    if (this.isForget()) {
      this.onForgotSubmit();
    } else {
      this.onLoginSubmit();
    }
  }

  private onLoginSubmit(): void {
    if (this.loginForm.valid) {
      const { username, password, rememberMe } =
        this.loginForm.value as { username: string; password: string; rememberMe: boolean };
      this.store.dispatch(AuthActions.login({ email: username, password, rememberMe: !!rememberMe }));
    } else {
      this.loginForm.markAllAsTouched();
    }
  }

  private onForgotSubmit(): void {
    const usernameCtrl = this.loginForm.get('username');
    if (usernameCtrl?.valid && usernameCtrl.value) {
      this.formLoading.set(true);
      this.authService.forgotPassword({ email: usernameCtrl.value, host: window.location.host })
        .subscribe({
          next: (r: any) => {
            this.formLoading.set(false);
            this.snackbar.open(r.message ?? 'Reset link sent. Check your inbox.', 'OK', { duration: 5000 });
          },
          error: (err: any) => {
            this.formLoading.set(false);
            const msg = err?.error?.errors?.[0]?.message ?? 'Something went wrong.';
            this.snackbar.open(msg, 'Dismiss', { duration: 5000 });
          },
        });
    } else {
      this.loginForm.get('username')?.markAsTouched();
      this.snackbar.open('Please enter a valid email address.', 'OK', { duration: 3500 });
    }
  }

  navigateToLogin(): void {
    this.router.navigate(['/login']);
  }

  navigateToForgot(): void {
    this.router.navigate(['/forgot-password']);
  }

}
